import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApi } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ChevronLeft, CircleDot, Circle } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";
import OrganiserDropdown, { displayName as organiserDisplay } from "@/components/OrganiserDropdown";
import MenuConfigEditor, {
  createEmptyMenuConfig, menuConfigFromRaw, validateMenuConfig,
} from "@/components/MenuConfigEditor";

/** Simple text-strip used for description-required validation. Matches
 *  mobile's stripHtml() by removing every tag then trimming. */
function stripHtml(html) {
  return String(html || "").replace(/<[^>]*>/g, "").trim();
}

function SectionCard({ title, hint, children, testId }) {
  return (
    <Card className="p-5 mb-4" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-widest text-primary mb-1">{title}</div>
      {hint && <div className="text-xs text-muted-foreground mb-3">{hint}</div>}
      {children}
    </Card>
  );
}

function OptionButton({ active, onClick, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={[
        "flex-1 px-4 py-2.5 rounded-md border-2 text-sm font-medium transition-colors",
        active ? "border-primary bg-primary/5 text-primary" : "border-border bg-white text-foreground hover:bg-muted/40",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function HostRadio({ host, active, onClick }) {
  const Icon = active ? CircleDot : Circle;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`tpl-acct-${host.host_id}`}
      className={[
        "w-full flex items-center gap-3 px-3 py-2.5 rounded-md border transition-colors text-left",
        active ? "bg-primary/5 border-primary" : "bg-white border-border hover:bg-muted/40",
      ].join(" ")}
    >
      <Icon size={18} className="text-primary shrink-0" />
      <div className="flex-1">
        <div className="text-sm font-medium">{host.name}</div>
        <div className="text-xs text-muted-foreground">
          {host.host_id}
          {host.has_live_key === false && " · no live key yet"}
          {host.has_test_key === false && " · no test key yet"}
        </div>
      </div>
    </button>
  );
}

export default function AdminTemplateNewPage() {
  const api = useApi();
  const nav = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;

  // Mandatory
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [organiser, setOrganiser] = useState("");
  const [organiserUserId, setOrganiserUserId] = useState(null);
  const [location, setLocation] = useState("");
  const [price, setPrice] = useState("0");
  const [capacity, setCapacity] = useState("50");
  const [maxPlaces, setMaxPlaces] = useState("1");

  // Attendee toggles
  const [useMasonicTitle, setUseMasonicTitle] = useState(false);
  const [useGrandRank, setUseGrandRank] = useState(false);
  const [useProvincialRank, setUseProvincialRank] = useState(false);
  const [useDietaryConcerns, setUseDietaryConcerns] = useState(false);

  // Event host
  const [accountLabel, setAccountLabel] = useState("PGLM");
  const [eventHosts, setEventHosts] = useState([]);

  // Menu + custom fields
  const [menuConfig, setMenuConfig] = useState(createEmptyMenuConfig());
  const [useBookingCustom, setUseBookingCustom] = useState(false);
  const [bookingCustomStr, setBookingCustomStr] = useState("");
  const [useAttendeeCustom, setUseAttendeeCustom] = useState(false);
  const [attendeeCustomStr, setAttendeeCustomStr] = useState("");

  // Complimentary codes
  const [complimentaryEnabled, setComplimentaryEnabled] = useState(false);

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await api("/event-hosts");
      if (r.ok) { const d = await r.json(); setEventHosts(d.event_hosts || []); }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      const r = await api(`/templates/${editId}`);
      if (!r.ok) return;
      const { template: t } = await r.json();
      setName(t.name || "");
      setDescription(t.description || "");
      setOrganiser(t.default_organiser || "");
      setOrganiserUserId(t.default_organiser_user_id || null);
      setUseMasonicTitle(!!t.use_masonic_title);
      setUseGrandRank(!!t.use_grand_rank);
      setUseProvincialRank(!!t.use_provincial_rank);
      setUseDietaryConcerns(!!t.use_dietary_concerns);
      setLocation(t.default_location || "");
      setPrice(((t.default_price_pence || 0) / 100).toString());
      setCapacity(String(t.default_capacity || 50));
      setMaxPlaces(String(t.max_places_per_booking || 1));
      setMenuConfig(menuConfigFromRaw(t.menu_config));
      const cfs = t.custom_fields || [];
      const bk = cfs.filter((c) => (c.scope || "booking") !== "attendee");
      const at = cfs.filter((c) => c.scope === "attendee");
      setUseBookingCustom(bk.length > 0);
      setBookingCustomStr(bk.map((c) => c.name).join(", "));
      setUseAttendeeCustom(at.length > 0);
      setAttendeeCustomStr(at.map((c) => c.name).join(", "));
      setAccountLabel(t.event_host_id || t.stripe_account_label || "PGLM");
      setComplimentaryEnabled(!!t.complimentary_codes_enabled);
    })();
    // eslint-disable-next-line
  }, [editId]);

  const validate = () => {
    if (!name.trim()) return "Template name is required.";
    if (!stripHtml(description)) return "Description is required.";
    if (!organiser.trim()) return "Default organiser is required.";
    if (!location.trim()) return "Default location is required.";
    if (!price.trim() || isNaN(parseFloat(price))) return "Default price is required (use 0 for free).";
    const cap = parseInt(capacity || "0", 10);
    if (!cap || cap < 1) return "Default capacity must be at least 1.";
    const mp = parseInt(maxPlaces || "0", 10);
    if (!mp || mp < 1 || mp > 12) return "Max places per booking must be between 1 and 12.";
    const menuErr = validateMenuConfig(menuConfig);
    if (menuErr) return menuErr;
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setSaving(true);
    try {
      const parseCf = (str, scope) => str.split(",").map((s) => s.trim()).filter(Boolean)
        .map((label) => ({ name: label, type: "text", required: false, scope }));
      const bookingFields = useBookingCustom ? parseCf(bookingCustomStr, "booking") : [];
      const attendeeFields = useAttendeeCustom ? parseCf(attendeeCustomStr, "attendee") : [];
      const body = {
        name: name.trim(),
        description,
        default_organiser: organiser.trim(),
        default_organiser_user_id: organiserUserId,
        use_masonic_title: useMasonicTitle,
        use_grand_rank: useGrandRank,
        use_provincial_rank: useProvincialRank,
        use_dietary_concerns: useDietaryConcerns,
        default_location: location.trim(),
        default_price_pence: Math.max(0, Math.round(parseFloat(price || "0") * 100)),
        default_capacity: Math.max(1, parseInt(capacity || "50", 10)),
        max_places_per_booking: Math.max(1, Math.min(12, parseInt(maxPlaces || "1", 10))),
        menu_config: menuConfig,
        menu_choices: menuConfig.mode === "simple" ? menuConfig.meal_options : [],
        custom_fields: [...bookingFields, ...attendeeFields],
        stripe_account_label: accountLabel,
        event_host_id: accountLabel,
        complimentary_codes_enabled: complimentaryEnabled,
        currency: "gbp",
      };
      const r = await api(isEdit ? `/templates/${editId}` : "/templates", {
        method: isEdit ? "PATCH" : "POST", body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) return toast.error(d.detail || "Save failed");
      toast.success(isEdit ? "Template updated" : "Template created");
      nav("/admin/templates");
    } finally { setSaving(false); }
  };

  return (
    <div>
      <button onClick={() => nav("/admin/templates")} className="text-sm text-primary inline-flex items-center gap-1 mb-4" data-testid="tplnew-back">
        <ChevronLeft size={16} /> Back to templates
      </button>
      <PageHeader title={isEdit ? "Edit template" : "New template"} testId="template-form-header" />

      <SectionCard title="Required details" hint="All fields below are mandatory on every template." testId="tpl-required-section">
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Template name <span className="text-destructive">*</span></Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Annual Festival Dinner" data-testid="tpl-name" />
          </div>
          <div className="space-y-1">
            <Label>Description <span className="text-destructive">*</span></Label>
            <div className="text-xs text-muted-foreground">Up to 1000 characters. Use the toolbar for bold, italics and lists.</div>
            <RichTextEditor value={description} onChange={setDescription} testId="tpl-desc" placeholder="Reusable description for this event series" />
          </div>
          <OrganiserDropdown
            label="Default organiser"
            required
            value={organiser}
            selectedUserId={organiserUserId}
            testId="tpl-org"
            hint="Only members promoted to Organiser or Admin appear in the list."
            onSelect={(u) => {
              if (u) { setOrganiser(organiserDisplay(u)); setOrganiserUserId(u.user_id); }
              else { setOrganiser(""); setOrganiserUserId(null); }
            }}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Default location <span className="text-destructive">*</span></Label>
              <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Freemasons' Hall, London" data-testid="tpl-loc" />
            </div>
            <div className="space-y-1">
              <Label>Default price (£) <span className="text-destructive">*</span></Label>
              <Input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0" data-testid="tpl-price" />
            </div>
            <div className="space-y-1">
              <Label>Default capacity <span className="text-destructive">*</span></Label>
              <Input inputMode="numeric" value={capacity} onChange={(e) => setCapacity(e.target.value)} placeholder="50" data-testid="tpl-cap" />
            </div>
            <div className="space-y-1">
              <Label>Max places per booking (1-12) <span className="text-destructive">*</span></Label>
              <Input inputMode="numeric" value={maxPlaces} onChange={(e) => setMaxPlaces(e.target.value)} placeholder="1" data-testid="tpl-maxp" />
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Event Host (Stripe account) *" hint="Selects which Stripe pot receives payment for events using this template." testId="tpl-hosts-section">
        <div className="space-y-2">
          {eventHosts.length === 0 ? (
            <div className="text-sm text-muted-foreground">Loading Event Hosts…</div>
          ) : eventHosts.map((h) => (
            <HostRadio key={h.host_id} host={h} active={accountLabel === h.host_id} onClick={() => setAccountLabel(h.host_id)} />
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Menu selection" testId="tpl-menu-section">
        <MenuConfigEditor value={menuConfig} onChange={setMenuConfig} />
      </SectionCard>

      <SectionCard
        title="Optional fields"
        hint="Per-booking fields are answered once per reservation. Per-attendee fields are asked once per guest, above their menu choice."
        testId="tpl-optional-section"
      >
        <label className="flex items-start gap-2 py-2">
          <Checkbox checked={useBookingCustom} onCheckedChange={(v) => setUseBookingCustom(!!v)} data-testid="tpl-use-booking-custom" />
          <div>
            <div className="text-sm font-medium">Per-booking custom fields</div>
            <div className="text-xs text-muted-foreground">Asked once per reservation (e.g. Lodge / Number)</div>
          </div>
        </label>
        {useBookingCustom && (
          <div className="pl-8 space-y-1 mb-2">
            <Label>Per-booking field names (comma separated)</Label>
            <Input value={bookingCustomStr} onChange={(e) => setBookingCustomStr(e.target.value)} placeholder="Lodge / Number" data-testid="tpl-booking-cf" />
          </div>
        )}

        <div className="mt-3 text-xs text-muted-foreground uppercase tracking-widest">Per-attendee custom fields</div>
        <div className="text-xs text-muted-foreground mb-2">
          These are asked per guest. Masonic options below pre-fill from each member&apos;s profile — tick them to include even if you don&apos;t add any free-form fields.
        </div>
        <div className="pl-2 space-y-1">
          {[
            { key: "mas", state: useMasonicTitle, set: setUseMasonicTitle, label: "Masonic Title", hint: "Bro / W.Bro / R.W.Bro etc. — from profile", testId: "tpl-use-masonic-title" },
            { key: "grand", state: useGrandRank, set: setUseGrandRank, label: "Grand Rank", hint: "From profile", testId: "tpl-use-grand-rank" },
            { key: "prov", state: useProvincialRank, set: setUseProvincialRank, label: "Provincial Rank", hint: "From profile", testId: "tpl-use-provincial-rank" },
            { key: "diet", state: useDietaryConcerns, set: setUseDietaryConcerns, label: "Dietary Concerns", hint: "Allergies / dietary requirements — pre-fills from profile", testId: "tpl-use-dietary-concerns" },
            { key: "extra", state: useAttendeeCustom, set: setUseAttendeeCustom, label: "Additional free-form fields", hint: "Extra fields asked per guest, comma separated (e.g. Mobile, Guest of, Notes)", testId: "tpl-use-attendee-custom" },
          ].map((row) => (
            <label key={row.key} className="flex items-start gap-2 py-1.5">
              <Checkbox checked={row.state} onCheckedChange={(v) => row.set(!!v)} data-testid={row.testId} />
              <div>
                <div className="text-sm font-medium">{row.label}</div>
                <div className="text-xs text-muted-foreground">{row.hint}</div>
              </div>
            </label>
          ))}
          {useAttendeeCustom && (
            <div className="pl-8 space-y-1 pt-1">
              <Label>Per-attendee field names (comma separated)</Label>
              <Input value={attendeeCustomStr} onChange={(e) => setAttendeeCustomStr(e.target.value)} placeholder="Mobile, Guest of" data-testid="tpl-attendee-cf" />
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard
        title="Complimentary Booking Codes"
        hint="Should events using this template offer complimentary booking codes? Codes are always generated on the individual event — the template only carries the enabled/disabled flag."
        testId="tpl-complimentary-section"
      >
        <div className="flex gap-2">
          <OptionButton active={!complimentaryEnabled} onClick={() => setComplimentaryEnabled(false)} testId="tpl-comp-off">No</OptionButton>
          <OptionButton active={complimentaryEnabled} onClick={() => setComplimentaryEnabled(true)} testId="tpl-comp-on">Yes</OptionButton>
        </div>
      </SectionCard>

      <div className="flex gap-2 pb-6">
        <Button onClick={save} disabled={saving} data-testid="tpl-save">{saving ? "Saving…" : (isEdit ? "Save changes" : "Save template")}</Button>
        <Button variant="ghost" onClick={() => nav("/admin/templates")}>Cancel</Button>
      </div>
    </div>
  );
}
